import { Box, Group, NumberInput, Select, Space, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';
import { useContext, useEffect, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import BookingStateItem from '../BookingStateItem';
import { EventsContext } from '../../../Service/Context/EventsContext';
import { ThemeText } from '../../../Atoms/ThemeText';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ConstantS } from '../../../Service/ConstantS';
import { GERMAN_MONTHS, getDateRangeFromSelection, getIntervalInputValues } from './dateIntervalSelectionUtils';

const DateIntervalSelection = () => {
  const { filter, updateFilter } = useContext(EventsContext);
  const { interval, from, to } = filter;
  const { sm } = useContext(WindowContext);
  const currentYear = new Date().getFullYear();
  const minYear = 2017;
  const maxYear = currentYear + 10;
  const currentIntervalInputValues = getIntervalInputValues(interval, from, to);
  const [selectedYear, setSelectedYear] = useState(currentIntervalInputValues.year);
  const [selectedMonth, setSelectedMonth] = useState(currentIntervalInputValues.month);
  const [yearInputVersion, setYearInputVersion] = useState(0);

  useEffect(() => {
    setSelectedYear(currentIntervalInputValues.year);
    setSelectedMonth(currentIntervalInputValues.month);
  }, [currentIntervalInputValues.month, currentIntervalInputValues.year]);

  useEffect(() => {
    if (selectedYear === currentIntervalInputValues.year && selectedMonth === currentIntervalInputValues.month) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      applyCustomInterval(selectedYear, selectedMonth);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [currentIntervalInputValues.month, currentIntervalInputValues.year, selectedMonth, selectedYear]);

  const applyPresetInterval = (nextInterval: typeof interval) => {
    MixpanelS.track(ConstantS.TrackingEvents.EventsFilterDateIntervalChanged, { interval: nextInterval });
    updateFilter({ interval: nextInterval, from: undefined, to: undefined });
  };

  const applyCustomInterval = (yearValue: string, monthValue: string) => {
    const nextRange = getDateRangeFromSelection(yearValue, monthValue);

    if (!nextRange) {
      return;
    }

    updateFilter({
      interval: 'ALL_TIME',
      from: nextRange.from,
      to: nextRange.to,
    });
  };

  const handleYearChange = (value?: number) => {
    const nextYear = value === undefined ? '' : value.toString().replace(/\D/g, '').slice(0, 4);
    setSelectedYear(nextYear);
  };

  const getYearStepBase = () => {
    const numericYear = Number(selectedYear);
    return selectedYear.length === 4 && !Number.isNaN(numericYear) ? numericYear : currentYear;
  };

  const handleYearStep = (step: 1 | -1) => {
    const nextYear = Math.min(Math.max(getYearStepBase() + step, minYear), maxYear);
    handleYearChange(nextYear);
    setYearInputVersion((version) => version + 1);
  };

  const handleYearKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleYearStep(event.key === 'ArrowUp' ? 1 : -1);
  };

  const handleYearControlPointerDown = (event: ReactPointerEvent<HTMLButtonElement>, step: 1 | -1) => {
    event.preventDefault();
    handleYearStep(step);
  };

  const isYearStepDisabled = (step: 1 | -1) => {
    const nextYear = getYearStepBase() + step;
    return nextYear < minYear || nextYear > maxYear;
  };

  const handleMonthChange = (value: string) => {
    const nextMonth = value.slice(0, 12);
    setSelectedMonth(nextMonth);
    if (nextMonth && !selectedYear) {
      setSelectedYear(new Date().getFullYear().toString());
    }
  };

  return (
    <>
      <Space h="xl" />
      <ThemeText pl={10}>Datumsbereich</ThemeText>
      <Space h="xs" />
      <Box px={10} pb="xs">
        <Group noWrap spacing={1}>
          <Select
            searchable
            clearable
            variant="filled"
            value={selectedMonth}
            onChange={(value) => handleMonthChange(value ?? '')}
            placeholder="Monat"
            data={GERMAN_MONTHS}
            rightSection={selectedMonth ? undefined : <></>}
          />
          <NumberInput
            key={yearInputVersion}
            value={selectedYear ? Number(selectedYear) : undefined}
            variant="filled"
            onChange={handleYearChange}
            placeholder="Jahr"
            min={minYear}
            max={maxYear}
            onKeyDownCapture={handleYearKeyDown}
            rightSection={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100% - 2px)',
                  margin: 1,
                  overflow: 'hidden',
                  width: 24,
                }}
              >
                <UnstyledButton
                  aria-label="Jahr erhöhen"
                  disabled={isYearStepDisabled(1)}
                  tabIndex={-1}
                  type="button"
                  onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) =>
                    handleYearControlPointerDown(event, 1)
                  }
                  sx={(theme) => ({
                    alignItems: 'center',
                    borderBottom: `1px solid ${
                      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
                    }`,
                    borderLeft: `1px solid ${
                      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
                    }`,
                    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                    display: 'flex',
                    flex: '0 0 50%',
                    justifyContent: 'center',
                    padding: 0,

                    '&:disabled': {
                      color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4],
                    },

                    '&:not(:disabled):hover': {
                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
                    },
                  })}
                >
                  <IconChevronUp size={14} />
                </UnstyledButton>
                <UnstyledButton
                  aria-label="Jahr verringern"
                  disabled={isYearStepDisabled(-1)}
                  tabIndex={-1}
                  type="button"
                  onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) =>
                    handleYearControlPointerDown(event, -1)
                  }
                  sx={(theme) => ({
                    alignItems: 'center',
                    borderLeft: `1px solid ${
                      theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
                    }`,
                    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                    display: 'flex',
                    flex: '0 0 50%',
                    justifyContent: 'center',
                    padding: 0,

                    '&:disabled': {
                      color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4],
                    },

                    '&:not(:disabled):hover': {
                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
                    },
                  })}
                >
                  <IconChevronDown size={14} />
                </UnstyledButton>
              </Box>
            }
            sx={{ maxWidth: 85, minWidth: 85 }}
          />
        </Group>
      </Box>
      {sm && (
        <>
          <BookingStateItem
            label="Letztes Jahr"
            onClick={() => applyPresetInterval('LAST_YEAR')}
            active={interval === 'LAST_YEAR'}
          />
          <BookingStateItem
            label="Dieses Jahr"
            onClick={() => applyPresetInterval('CURRENT_YEAR')}
            active={interval === 'CURRENT_YEAR'}
          />
          <BookingStateItem
            label="Letzter Monat"
            onClick={() => applyPresetInterval('LAST_MONTH')}
            active={interval === 'LAST_MONTH'}
          />
        </>
      )}

      <BookingStateItem
        label="Dieser Monat"
        onClick={() => applyPresetInterval('CURRENT_MONTH')}
        active={interval === 'CURRENT_MONTH'}
      />
      <BookingStateItem
        label="Nächste Events"
        onClick={() => applyPresetInterval('FUTURE')}
        active={interval === 'FUTURE'}
      />
      <BookingStateItem
        label="Nächster Monat"
        onClick={() => applyPresetInterval('NEXT_MONTH')}
        active={interval === 'NEXT_MONTH'}
      />
    </>
  );
};

export default DateIntervalSelection;
